import { useAtom } from "jotai";
import tools from "../../../../../../helpers/tools";
import BrandLogo from "../../../../Brand/BrandLogo";
import EditableCell from "../../FlotteSimulator/EditableCell";
import FlotteStatutBadge from "../../FlotteStatutBadge";
import { DateDebut, DateFin } from "../../PeriodContratCell";
import { columnsAtom } from "../useFlotteTable";
import { getFacturationLock, isVORole } from "../FlotteUtils";
import { connectedUserAtom } from "../../../../../../middlewares/AuthMiddleware";
import CellCondensed from "./CellCondensed";
import LastCommentBubble from "./LastCommentBubble";
import AnomalieFacturationBubble from "./AnomalieFacturationBubble";
import LicensePlate from "../../../../../common/LicensePlate/LicensePlate";

/**
 * Récupère la valeur d'une propriété dans un objet en utilisant un chemin (dot notation)
 * @param {Object} obj - L'objet source
 * @param {string} path - Le chemin vers la propriété (ex: "contrat.duree_mois")
 * @returns {any} La valeur trouvée ou undefined
 */
const getValueByPath = (obj, path) => {
  if (!path || !obj) return undefined;
  return path.split(".").reduce((current, key) => current?.[key], obj);
};

/**
 * Composant de rendu dynamique des cellules basé sur le type
 * @param {string} type - Le type de cellule
 * @param {any} value - La valeur à afficher
 * @param {Object} item - L'objet complet du véhicule (pour les cas spéciaux)
 * @param {Function} toggle - Fonction de toggle pour les actions
 * @param {boolean} isOpen - État d'ouverture pour les actions
 * @returns {JSX.Element} L'élément de cellule rendu
 */
export const renderCellByType = (
  type,
  value,
  item,
  toggle,
  isOpen,
  isRowSelected,
  toggleRowSelection,
) => {
  switch (type) {
    case "actions": {
      return (
        <div className="d-flex align-items-center">
          <input
            type="checkbox"
            className=" mr-2"
            checked={isRowSelected?.(item.id) || false}
            onChange={() => toggleRowSelection?.(item.id)}
            onClick={(e) => e.stopPropagation()}
          />

          <LastCommentBubble commentaires={item.commentaires} />
          <AnomalieFacturationBubble item={item} />
          <span>
            {item.position != null && (
              <span className="badge badge-warning ml-2">{item.position}</span>
            )}
            {item.localisation != null && (
              <span className="badge badge-info ml-2">
                {item.localisation.replace(/^AGENCE_/, "").replace(/_/g, " ")}
              </span>
            )}
          </span>
        </div>
      );
    }

    case "badge": {
      // Historiquement, "badge" était utilisé pour les statuts (numériques)
      // On le rend générique pour supporter des badges texte (ex: catalogue)
      if (value === null || value === undefined || value === "") return "";

      const numericValue = parseInt(value);
      const isNumeric =
        !Number.isNaN(numericValue) && `${numericValue}` === `${value}`;

      if (isNumeric) {
        return <FlotteStatutBadge statut={numericValue} />;
      }

      const normalizedValue = `${value}`.trim().toLowerCase();
      const badgeBgColor =
        normalizedValue === "reunion"
          ? "#007bff"
          : normalizedValue === "mayotte"
            ? "#8b5a2b"
            : "#6c757d";

      return (
        <span
          className="badge"
          style={{ backgroundColor: badgeBgColor, color: "#fff" }}
        >
          {value}
        </span>
      );
    }

    case "brand":
      return <BrandLogo brandName={value} showName />;

    case "immatriculation":
      return <LicensePlate value={value} />;

    case "text":
      return value || "";

    case "condensed":
      return <CellCondensed>{value || ""}</CellCondensed>;

    case "currency":
      return tools.formatCurrency(value);

    case "percentage":
      return tools.formatPercentage(value);

    case "date":
      return tools.formatDate(value);

    case "date_debut":
      return <DateDebut dateDebut={value} />;

    case "date_fin":
      return <DateFin dateFin={value} />;

    case "duration":
      return (
        <span className="font-weight-bold" style={{ whiteSpace: "nowrap" }}>
          {value ? `${value} mois` : ""}
        </span>
      );

    case "distance":
      return (
        <span className="font-weight-bold" style={{ whiteSpace: "nowrap" }}>
          {value ? `${tools.beautiFyNumberformatNumberSpaces(value)} km` : ""}
        </span>
      );

    default:
      return value || "";
  }
};

export const renderEditableCell = (columnKey, value, item, onUpdateCell) => {
  return (
    <EditableCell field={columnKey} value={value} updateCell={onUpdateCell} />
  );
};
/**
 * Composant Cells refactorisé pour utiliser columnsConfig dynamiquement
 * @param {string} type - Le type/clé de la colonne
 * @param {Function} toggle - Fonction de toggle pour les actions
 * @param {Object} item - L'objet véhicule
 * @param {boolean} isOpen - État d'ouverture pour les actions
 * @returns {JSX.Element} L'élément de cellule
 */
const Cells = ({
  type,
  toggle,
  item,
  isOpen,
  onUpdateCell,
  onDeverrouillerFacturation,
  isRowSelected,
  toggleRowSelection,
}) => {
  const columnKey = type.toString();
  const [columnsConfig] = useAtom(columnsAtom);
  const [connectedUser] = useAtom(connectedUserAtom);
  const columnConfig = columnsConfig[columnKey];

  // Si la configuration n'existe pas, on retourne null
  if (!columnConfig) {
    return null;
  }

  // Récupération de la valeur basée sur le path
  const value = getValueByPath(item, columnConfig.path);

  // Verrouiller le prix de vente définitif et la date VO si le véhicule est au statut 61+
  const LOCKED_FIELDS_ON_STATUT_61 = ["vo_prix_vente_definitif", "vo_date_vo"];
  const isLockedByStatut =
    LOCKED_FIELDS_ON_STATUT_61.includes(columnKey) &&
    isVORole(connectedUser?.role) &&
    ((parseInt(item?.statut) >= 61 && parseInt(item?.statut) < 531) ||
      parseInt(item?.statut) >= 631);

  // LS-738 : date de facture, client facturé et prix facturé sont figés pour tout le monde
  // dès qu'un import de facturation sans anomalie les a validés (remplace l'ancien verrou
  // par statut ≥ 61 du seul prix facturé). Les rôles VO ne les éditent jamais : voir
  // editableVO / editableVO531 dans flotteColumns.config.
  const facturationLock = getFacturationLock(columnKey, item);
  const peutDeverrouiller =
    facturationLock.locked &&
    connectedUser?.role === "SUPER_ADMIN" &&
    typeof onDeverrouillerFacturation === "function";

  const isEditable =
    columnConfig.editable && !isLockedByStatut && !facturationLock.locked;

  // Rendu de la cellule basé sur le type
  const cellContent = isEditable
    ? renderEditableCell(columnKey, value, item, onUpdateCell)
    : renderCellByType(
        columnConfig.type,
        value,
        item,
        toggle,
        isOpen,
        isRowSelected,
        toggleRowSelection,
      );

  return (
    <td>
      <div className="position-relative">
        {cellContent}

        {facturationLock.locked && (
          <span
            className="text-muted"
            style={{ fontSize: 10, marginLeft: 4, whiteSpace: "nowrap" }}
            title={facturationLock.title}
          >
            <i className="fas fa-lock"></i>
            {peutDeverrouiller && (
              <button
                type="button"
                className="btn btn-link p-0"
                style={{ fontSize: 10, marginLeft: 4, verticalAlign: "baseline" }}
                title="Déverrouiller la facturation (super admin)"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeverrouillerFacturation(item);
                }}
              >
                <i className="fas fa-unlock text-warning"></i>
              </button>
            )}
          </span>
        )}

        {columnConfig.isSync && (
          <span
            className="position-absolute "
            style={{
              top: -8,
              left: 0,
              fontSize: 8,
            }}
            title="Synchronisé avec Hitech"
          >
            <i className="fas fa-sync-alt text-info"></i>
          </span>
        )}
      </div>
    </td>
  );
};

export default Cells;
